import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../contexts/StoreContext';
import { Gallery } from '../components/vehicle/Gallery';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { OfferStatus } from '../components/OfferStatus';
import { FinanceConditions } from '../components/FinanceConditions';
import { CircularTable } from '../components/simulator/CircularTable';
import { eligibleLines, managementFeeText } from '../utils/circular';
import { InquiryForm } from '../components/InquiryForm';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { getQuickQuote } from '../utils/finance';
import { vehicleBrandName } from '../utils/catalog';
import { formatARS, formatDate, formatPct } from '../utils/format';
function Spec({
  label,
  value
}: {
  label: string;
  value: string | null;
}) {
  return <div className="py-2.5 sm:grid sm:grid-cols-[180px_1fr] sm:gap-4" data-id="element-1397" data-mp="2lfoauj3yhxkh">
      <dt className="text-sm text-mist" data-id="element-1398" data-mp="2lhsklhojdqso">{label}</dt>
      <dd className={`text-sm ${value ? 'text-white' : 'italic text-mist/70'}`} data-id="element-1399" data-mp="2hdb4bshdxvmn">{value ?? 'No disponible'}</dd>
    </div>;
}
function ListBlock({
  items
}: {
  items: string[];
}) {
  if (!items.length) return <p className="text-sm italic text-mist/70" data-id="element-1400" data-mp="2qgc8hnmoxcno">No disponible</p>;
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1401" data-mp="22owz5awhxv2x">
      {items.map(i => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1402" data-mp="1xqdgpkh431fb">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon" aria-hidden data-id="element-1403" data-mp="1xs6myyntbjms" />
          {i}
        </li>)}
    </ul>;
}
export function VehicleDetail() {
  const {
    id
  } = useParams();
  const {
    vehicles,
    brands,
    compareIds,
    toggleCompare,
    catalogSync
  } = useStore();
  const vehicle = vehicles.find(v => v.id === id && v.published);
  const quote = useMemo(() => vehicle ? getQuickQuote(vehicle, brands) : null, [vehicle, brands]);
  if (!vehicle && catalogSync.status === 'loading') {
    return <div className="container-x py-24 text-center" role="status" data-id="element-1404" data-mp="3f00v8ox1xfe7">
        <p className="font-display text-xl font-bold" data-id="element-1405" data-mp="3jzx00qdkpaut">Cargando unidad…</p>
        <p className="mt-2 text-sm text-mist" data-id="element-1406" data-mp="3orokqka1smd1">Estamos consultando el inventario actualizado.</p>
      </div>;
  }
  if (!vehicle) {
    return <div className="container-x py-24 text-center" data-id="element-1407" data-mp="3svmh4n7znj2e">
        <h1 className="font-display text-2xl font-bold" data-id="element-1408" data-mp="2w4aut64ror2f">Unidad no disponible</h1>
        <p className="mt-2 text-mist" data-id="element-1409" data-mp="311u6bya2ypqo">Puede que haya sido dada de baja o que el enlace sea incorrecto.</p>
        <Link to="/catalogo" className="btn btn-primary mt-6" data-id="element-1410" data-mp="353o5u38jhnp9">Volver al catálogo</Link>
      </div>;
  }
  const brand = brands.find(b => b.id === vehicle.brandId);
  const title = `${vehicleBrandName(vehicle, brands)} ${vehicle.model}`;
  const inCompare = compareIds.includes(vehicle.id);
  return <div className="container-x py-6 lg:py-10" data-id="element-1411" data-mp="pc07q4hshp4v">
      <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm text-mist hover:text-white" data-id="element-1412" data-mp="pew59rpq6bss">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden data-id="element-1413" data-mp="ziku7nkjyjcn" /> Catálogo
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_400px]" data-id="element-1414" data-mp="uif3c9m3x0vm">
        <div className="min-w-0" data-id="element-1415" data-mp="ppvjiuwtt9id">
          <Gallery images={vehicle.images} alt={title} data-id="element-1416" data-mp="pou64jsc8axh" />

          <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-1417" data-mp="loa9dhbof9bv">
            <AvailabilityBadge value={vehicle.availability} data-id="element-1418" data-mp="gpyz55hgywt4" />
            <span className="text-xs text-mist" data-id="element-1419" data-mp="grjtov3safqz">{vehicle.segment} · {vehicle.bodyType} · Año/modelo {vehicle.year}</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1420" data-mp="6rsyer7efwpu">{title}</h1>
          <p className="text-lg text-mist" data-id="element-1421" data-mp="2qqye85nrgmt">{vehicle.version}</p>

          <section className="mt-10" aria-labelledby="s-motor" data-id="element-1422" data-mp="2sk5fok6o2vc">
            <h2 id="s-motor" className="font-display text-lg font-bold" data-id="element-1423" data-mp="2152ckwaw46ef">Motorización</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1424" data-mp="1x48oo2xg6rlj">
              <Spec label="Motor" value={vehicle.engine} data-id="element-1425" data-mp="1x2ntcb3b74mm" />
              <Spec label="Transmisión" value={vehicle.transmission} data-id="element-1426" data-mp="249020ieu2ssa" />
              <Spec label="Consumo" value={vehicle.consumption} data-id="element-1427" data-mp="297b9imhnor7c" />
            </dl>
          </section>

          <section className="mt-10" aria-labelledby="s-seg" data-id="element-1428" data-mp="1v7jt6f3o2yq6">
            <h2 id="s-seg" className="mb-3 font-display text-lg font-bold" data-id="element-1429" data-mp="1v9cxvoupz8vj">Seguridad</h2>
            <ListBlock items={vehicle.safety} data-id="element-1430" data-mp="1zaosk8sgakal" />
          </section>

          <section className="mt-10" aria-labelledby="s-eq" data-id="element-1431" data-mp="1lgfb36ip2xr4">
            <h2 id="s-eq" className="mb-3 font-display text-lg font-bold" data-id="element-1432" data-mp="1q7nmu97hyv7g">Equipamiento</h2>
            <ListBlock items={vehicle.equipment} data-id="element-1433" data-mp="1bj19ue5cefje" />
          </section>

          <section className="mt-10" aria-labelledby="s-gar" data-id="element-1434" data-mp="1bkuczjgqyhmn">
            <h2 id="s-gar" className="font-display text-lg font-bold" data-id="element-1435" data-mp="1gj5qq5a5xca5">Garantía y disponibilidad</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1436" data-mp="12je45g5kynkj">
              <Spec label="Garantía" value={vehicle.warranty} data-id="element-1437" data-mp="17hfzazga6dcd" />
              <Spec label="Disponibilidad" value={vehicle.availability} data-id="element-1438" data-mp="17gwhdiqmroyc" />
              <Spec label="Vigencia de la oferta" value={quote ? `Hasta el ${formatDate(quote.ctx.offer.validUntil)}` : null} data-id="element-1439" data-mp="nwp6zkyswmhb" />
              <Spec label="Datos actualizados" value={formatDate(vehicle.updatedAt)} data-id="element-1440" data-mp="iy493s70uew1" />
            </dl>
          </section>

          {quote && <section className="mt-10" aria-labelledby="s-fin" data-id="element-1441" data-mp="suz1rugm5l2e">
              <h2 id="s-fin" className="font-display text-lg font-bold" data-id="element-1442" data-mp="swtp1ffcxp9n">Condiciones financieras</h2>
              <div className="mt-2" data-id="element-1443" data-mp="16sf8z86qhiv7">
                <OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1444" data-mp="11vx8wlmcm8p7" />
              </div>
              <div className="mt-3" data-id="element-1445" data-mp="1gktd27lab1kj">
                {quote.ctx.circular ? <>
                    <p className="mb-3 text-sm text-mist" data-id="element-1446" data-mp="1bkozapbrgky0">
                      {quote.ctx.circular.circular.lender} · {quote.ctx.circular.circular.reference}. Cuotas con IVA incluido, sin seguro automotor. {managementFeeText(quote.ctx.circular.circular)}
                    </p>
                    <CircularTable circular={quote.ctx.circular.circular} lines={eligibleLines(quote.ctx.circular.circular, vehicle)} selected={{
                lineId: quote.ctx.circular.line.id,
                term: quote.ctx.circular.row.term
              }} data-id="element-1447" data-mp="i8qv44b2e6n5" />
                  </> : <FinanceConditions offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1448" data-mp="w1ghwayf3flb" />}
              </div>
            </section>}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-1449" data-mp="15xa46m1xunga">
          <div className="card p-5" data-id="element-1450" data-mp="1ax65uen5y2so">
            <p className="text-xs text-mist" data-id="element-1451" data-mp="1ay7lyxj1dfh9">Precio de contado</p>
            <p className="font-display text-3xl font-black tracking-tight" data-id="element-1452" data-mp="1eyhpm3jcvtsy">{formatARS(vehicle.price)}</p>
            {quote?.sim && <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4 text-sm" data-id="element-1453" data-mp="3cpkkn4ueur4v">
                <div data-id="element-1454" data-mp="3gr5u0g7qknab"><dt className="text-[11px] text-mist" data-id="element-1455" data-mp="3gr5yaserr9g4">Anticipo requerido</dt><dd className="font-semibold" data-id="element-1456" data-mp="396bquzkqp9og">{formatARS(quote.down)}</dd></div>
                <div data-id="element-1457" data-mp="398g5abg9lr2z"><dt className="text-[11px] text-mist" data-id="element-1458" data-mp="398g78gzts15m">Capital a financiar</dt><dd className="font-semibold" data-id="element-1459" data-mp="34f3k6baso2ez">{formatARS(quote.principal)}</dd></div>
                <div data-id="element-1460" data-mp="2zhttytfkzmq3"><dt className="text-[11px] text-mist" data-id="element-1461" data-mp="2zhts0nw0tcng">Cuotas estimadas</dt><dd className="font-semibold text-neon" data-id="element-1462" data-mp="2vf6vwasbh5kg">{quote.term} × {formatARS(quote.sim.firstInstallment)}</dd></div>
                <div data-id="element-1463" data-mp="3s5pphutez8la"><dt className="text-[11px] text-mist" data-id="element-1464" data-mp="3s5prg0cz5inx">TNA / CFT</dt><dd className="font-semibold" data-id="element-1465" data-mp="3s3d7jhnnoy88">{formatPct(quote.ctx.offer.tna)} / {formatPct(quote.ctx.offer.cft, 'N/I')}</dd></div>
              </dl>}
            {quote && <div className="mt-3" data-id="element-1466" data-mp="3j3823d20w9wp"><OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} compact data-id="element-1467" data-mp="3j6vxyiuxx8el" /></div>}
            <div className="mt-5 grid gap-2" data-id="element-1468" data-mp="3e5eymrs3tu04">
              <Link to={`/simulador?v=${vehicle.id}`} className="btn btn-primary" data-id="element-1469" data-mp="27q5lwj3u5md2">SIMULAR FINANCIACIÓN</Link>
              <WhatsAppButton vehicle={vehicle} data-id="element-1470" data-mp="22q9s1courbb8" />
              <button onClick={() => {
              const r = toggleCompare(vehicle.id);
              if (r === 'full') toast.error('Podés comparar hasta 3 vehículos.');
            }} className="btn btn-ghost" aria-pressed={inCompare} data-id="element-1471" data-mp="22sm9zpum1loa">
                {inCompare ? <CheckIcon className="h-4 w-4" data-id="element-1472" data-mp="1o1vydpocqtr" /> : <PlusIcon className="h-4 w-4" data-id="element-1473" data-mp="1pd2glai8cok" />}
                {inCompare ? 'En el comparador' : 'Agregar al comparador'}
              </button>
            </div>
          </div>

          <div className="card p-5" data-id="element-1474" data-mp="udha3ccmyztw">
            <h2 className="font-display text-lg font-bold" data-id="element-1475" data-mp="ucfsep147f37">Consultar por esta unidad</h2>
            <p className="mb-4 mt-1 text-sm text-mist" data-id="element-1476" data-mp="fn9vj7pr20yh">Un asesor comercial te responde con disponibilidad y condiciones actualizadas.</p>
            <InquiryForm channel="vehiculo" vehicle={vehicle} allowQuote idPrefix="vd" data-id="element-1477" data-mp="kktcifdu0ru4" />
          </div>
        </aside>
      </div>
    </div>;
}